import { FilterField, useSubdivisionOptions } from "~/features/filter";
import {
  FilterExistenceType,
  ViolationStatus,
  ViolationTypeCategory,
} from "~/schemas/generated/graphql";

import {
  useDealerOptions,
  useFederalAreaOptions,
  useRegionOptions,
  useViolationTypeOptions,
} from "./optionsHooks";

type Translate = (key: string) => string;

/**
 * Поля фильтра страницы нарушений. Раскладка — две строки на всю ширину:
 * первая (документ, статус, ФО, регион, срок исполнения) и вторая (остальное)
 * имеют разное число колонок, поэтому вторая строка начинается с `newRow`.
 */
export const violationsFilterFields = (
  t: Translate,
  isDealer: boolean,
): FilterField[] => {
  const firstRow: FilterField[] = [
    {
      name: "documentFilter",
      type: "select",
      label: t("violationsTable.filterLabelDocument"),
      options: [
        {
          value: FilterExistenceType.Exist,
          label: t("violationsTable.filterPositive"),
        },
        {
          value: FilterExistenceType.NotExist,
          label: t("violationsTable.filterNegative"),
        },
      ],
    },
    {
      name: "statusFilter",
      type: "select",
      label: t("violationsTable.filterLabelStatus"),
      options: [
        ViolationStatus.ToFix,
        ViolationStatus.GotDealerResponse,
        ViolationStatus.Fixed,
        ViolationStatus.HasComments,
        ViolationStatus.Reject,
        ViolationStatus.Deleted,
      ].map((value) => ({ value, label: t(`violationsTable.${value}`) })),
    },
    ...(!isDealer
      ? [
          {
            name: "FoFilter",
            type: "select",
            label: t("violationsTable.filterLabelFO"),
            optionsHook: useFederalAreaOptions,
          } as FilterField,
          {
            name: "regionFilter",
            type: "select",
            label: t("violationsTable.filterLabelRegion"),
            optionsHook: useRegionOptions,
          } as FilterField,
        ]
      : []),
    {
      name: "fromFilter",
      nameTo: "toFilter",
      type: "dateRange",
      label: t("violationsTable.filterLabelDate"),
      placeholder: t("violationsTable.defaultDateFrom"),
      placeholderTo: t("violationsTable.defaultDateTo"),
      fixedWidth: 360,
    },
  ];

  const secondRow: FilterField[] = [
    ...(!isDealer
      ? [
          {
            name: "dealerIdFilter",
            type: "select",
            label: t("violationsTable.filterLabelDealership"),
            optionsHook: useDealerOptions,
          } as FilterField,
          {
            name: "subdivisionIdFilter",
            type: "select",
            label: t("violationsTable.filterLabelSubdivision"),
            optionsHook: useSubdivisionOptions,
          } as FilterField,
        ]
      : []),
    {
      name: "violationTypesIdFilter",
      type: "select",
      label: t("violationsTable.filterLabelViolationType"),
      optionsHook: useViolationTypeOptions,
    },
    {
      name: "categoryFilter",
      type: "select",
      label: t("violationsTable.filterLabelCategory"),
      options: [
        {
          value: ViolationTypeCategory.Critical,
          label: t("violationsTable.critical"),
        },
        {
          value: ViolationTypeCategory.Operational,
          label: t("violationsTable.operational"),
        },
        {
          value: ViolationTypeCategory.Minor,
          label: t("violationsTable.minor"),
        },
      ],
    },
    {
      name: "dealerCandidate",
      type: "select",
      label: t("violationsTable.dealerCandidate"),
      options: [
        { value: "active", label: t("violationsTable.activeCandidate") },
        { value: "candidate", label: t("violationsTable.selectCandidate") },
      ],
    },
    {
      name: "fromCreatedAt",
      nameTo: "toCreatedAt",
      type: "dateRange",
      label: t("violationsTable.filterLabelDateRange"),
      placeholder: t("violationsTable.defaultDateFrom"),
      placeholderTo: t("violationsTable.defaultDateTo"),
      fixedWidth: 360,
    },
  ];

  // Принудительный перенос: вторая строка всегда начинается с новой строки грида
  secondRow[0] = { ...secondRow[0], newRow: true };

  return [...firstRow, ...secondRow];
};
